import { NERVA_PARTNER_INSURERS, NervaPartnerInsurer, TrialPlan } from 'hydra-data-types';
import { ActionType } from '../store/ActionTypes';
import { StoreAction } from '../store/Store';
import { UserDataState } from '../store/userData/UserDataState';
import { Environment } from './Environment';

/** The no-cost access agreement for an insurer-dropdown answer, if the insurer has one. */
export function getPartnerInsurer(insurer: string | undefined): NervaPartnerInsurer | undefined {
  return NERVA_PARTNER_INSURERS.find((partner) => partner.insurer === insurer);
}

type PartnerInsurerUserData = Pick<
  UserDataState,
  'usedPartnerInsurance' | 'partnerInsurer' | 'partnerInsurerMemberId'
>;

/** Whether the user chose "Use my <insurer> access". */
export function hasUsedPartnerInsurerAccess(userData: UserDataState): boolean {
  return userData.usedPartnerInsurance?.[0] === 'Yes';
}

/** What the user did on the partner-insurer screen. */
export type PartnerInsurerAccessSubmission =
  | { action: 'use'; memberId: string }
  | { action: 'continueWithout' };

/**
 * What the partner-insurer screen saves. Continuing without access clears any member ID given
 * earlier, so it isn't shared with the insurer.
 */
export function getPartnerInsurerAccessUserData(
  partner: NervaPartnerInsurer,
  submission: PartnerInsurerAccessSubmission
): PartnerInsurerUserData {
  const used = submission.action === 'use';
  return {
    usedPartnerInsurance: [used ? 'Yes' : 'No'],
    partnerInsurer: [partner.insurer],
    partnerInsurerMemberId: used ? [submission.memberId] : [],
  };
}

/**
 * Withdraws partner access taken earlier when the insurer screen is answered some other way, so the
 * member ID isn't shared with the insurer. Users who were never offered access get nothing written.
 */
export function getPartnerInsurerAccessWithdrawal(userData: UserDataState): PartnerInsurerUserData {
  return userData.usedPartnerInsurance === undefined
    ? {}
    : { usedPartnerInsurance: ['No'], partnerInsurerMemberId: [] };
}

/**
 * Staging calls the production backend, which uses live Stripe, so only development builds take the
 * test-mode code.
 */
function getPartnerInsurerCouponCode(partner: NervaPartnerInsurer): string {
  return Environment.DEVELOPMENT ? partner.promotionCode.dev : partner.promotionCode.prod;
}

/** What "Use my <insurer> access" dispatches: the partner's 100%-off code and no trial. */
export function getPartnerInsurerAccessActions(partner: NervaPartnerInsurer): StoreAction[] {
  return [
    { type: ActionType.SET_COUPON_CODE, couponCode: getPartnerInsurerCouponCode(partner) },
    { type: ActionType.SET_SELECTED_TRIAL_PLAN, trialPlan: TrialPlan.None },
  ];
}
